import type { ChoreReaction } from '../../../../../shared/types';

/** Row shape as the API serializes it (SQLite 0/1 never reaches the client —
 *  booleans stay booleans, timestamps are numbers). */
export interface ChoreItem {
  choreId: string;
  title: string;
  cadence: 'once' | 'recurring';
  freq: 'daily' | 'weekly' | 'monthly' | null;
  /** Which rule rolls the date forward (card VmoORelj1HuO). */
  recurMode: 'fixed' | 'interval' | 'completion';
  /** Step in days for interval / completion, null for fixed. */
  recurEveryDays: number | null;
  assigneeId: string | null;
  /** Who governs the assignee (card UeOwZSHfdZCh): fixed, claim, alternate. */
  assignMode: 'fixed' | 'claim' | 'alternate';
  dueAt: number | null;
  status: 'open' | 'done';
  completedAt: number | null;
  completedBy: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** False when it was created with the "Add to Calendar & Tasks" switch off. */
  syncGoogle: boolean;
  /** Times this occurrence has been pushed out (snooze or defer) — 3 is the
   *  cap, after which the panel offers a change of plan instead (xvom08w4BgGT). */
  snoozeCount: number;
  /** Public reactions on this card (MnorjNDwB3of) — at most one per member. */
  reactions: ChoreReaction[];
  /** The warm receipt left at completion ("done — out of bin liners"). */
  doneNote: string | null;
  /** Who left the receipt — completer or partner. */
  doneNoteBy: string | null;
}

/** The couple's list-clear streak (GET /api/chores/streak). Couple-scoped:
 *  no member id is ever part of this shape. */
export interface ChoreStreak {
  streak: number;
  lastClearWeek: number | null;
  lastClearAt: number | null;
}

/** The three pushes the "Later" chooser offers, as offsets from now. Each is
 *  a real time rather than a date string, so the worker never has to guess a
 *  timezone — and every one of them is safely in the future. */
export const SNOOZE_CHOICES: { key: string; label: string; ms: number }[] = [
  { key: 'tomorrow', label: 'Tomorrow', ms: 24 * 3_600_000 },
  { key: 'in3', label: 'In 3 days', ms: 3 * 24 * 3_600_000 },
  { key: 'nextweek', label: 'Next week', ms: 7 * 24 * 3_600_000 },
];

/** error.code out of the JSON body `api()` throws — null when the message is
 *  not parseable JSON, so a network failure never reads as a code. */
export function errCode(e: unknown): string | null {
  try {
    const parsed = JSON.parse((e as Error)?.message ?? '');
    return parsed?.error?.code ?? null;
  } catch {
    return null;
  }
}

/** GET /api/chores/google/status — configured tells the panel whether this
 *  worker has a Google client at all; tokens are never part of this shape. */
export interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  provider: string | null;
  accountEmail: string | null;
  scopes: string[];
  connectedAt: number | null;
}

export const CADENCE_OPTIONS: { value: 'once' | 'recurring'; label: string }[] = [
  { value: 'once', label: '1x' },
  { value: 'recurring', label: 'Recurring' },
];

export const FREQ_OPTIONS: { value: string; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

/** The three rules a recurring chore can carry (card VmoORelj1HuO): the
 *  classic fixed cadence, a straight N-day interval kept on schedule, and a
 *  step counted from the tick itself. */
export type RecurMode = 'fixed' | 'interval' | 'completion';

export const RECUR_OPTIONS: { value: RecurMode; label: string }[] = [
  { value: 'fixed', label: 'On a cadence' },
  { value: 'interval', label: 'Every N days' },
  { value: 'completion', label: 'After it is done' },
];

/** Who the create form assigns the chore to. The row's own pill cycles the
 *  same three states afterwards; this is the choice made BEFORE it exists. */
export type AssigneeChoice = 'me' | 'partner' | 'anyone';

/** How the row decides who is on it (card UeOwZSHfdZCh). */
export type AssignMode = 'fixed' | 'claim' | 'alternate';

export const ASSIGN_OPTIONS: { value: AssignMode; label: string }[] = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'claim', label: 'Claim' },
  { value: 'alternate', label: 'Alternate' },
];

/** What the mode says in one line — shown under the chips, because a mode
 *  nobody can read is a mode nobody will pick. */
export const ASSIGN_HINT: Record<AssignMode, string> = {
  fixed: 'Whoever you put on it stays on it.',
  claim: 'Nobody until they take it — first tap wins.',
  alternate: 'Whoever finishes hands it to the other one.',
};
